/* ============================================================================
 * universal-search.js  —  FSM-44 Universal Search (Phase 1)
 * ----------------------------------------------------------------------------
 * Adds a grouped predictive-search dropdown to the header search box and a
 * grouped results block on the /search page, using Shopify's NATIVE Predictive
 * Search API (/search/suggest.json). No third-party app, no jQuery.
 *
 * Categories (Phase 1): Products, Collections, Pages, Blogs.
 *   - Store Locations = Phase 2 (not implemented here).
 *   - On /search, PRODUCTS are rendered natively by sections/search.liquid
 *     (untouched). This script only adds the dropdown everywhere, plus the
 *     secondary groups (Collections / Pages / Blogs) on the results page.
 *
 * REVERSIBILITY (FSM-44): to fully remove this feature, delete this file and
 * universal-search.css, then remove the fenced "UNIVERSAL SEARCH (FSM-44)"
 * blocks in layout/theme.liquid, sections/header.liquid and
 * sections/search.liquid. The original products-only search is then intact.
 *
 * CONFIG: tweak the CONFIG object below (limits, debounce, excluded pages).
 * ==========================================================================*/
(function () {
  'use strict';

  var CONFIG = {
    minChars: 2,            // start searching after this many characters
    debounceMs: 250,        // wait after typing before firing a request
    dropdownLimit: 4,       // items per group in the header dropdown
    pageLimit: 10,          // items per group on the /search results page
    resources: 'product,collection,page,article',
    // FSM-44: predictive search defaults to only title,product_type,variants.title,vendor —
    // so products that match only on description/tags/SKU (which the full /search page DOES
    // find) were missing from the dropdown. Widen the fields to match full search.
    fields: 'title,product_type,variants.title,vendor,tag,body,variants.sku',
    // FSM-44 (deterministic): the PRODUCTS group is powered by a local, comprehensive
    // product index (sections/search-index.liquid) fetched once per session and matched
    // with a STRICT-AND rule — every typed word must appear somewhere in the product.
    // Fully predictable; searches ALL fields incl. tags, every variant/SKU, options,
    // collections, description, and metafields. Predictive Search is used only for the
    // non-product groups (collections / pages / blogs), which are not the concern here.
    indexUrl: '/?section_id=search-index',
    // Utility/policy pages we never surface as content results.
    // (about-us and contact-us ARE intentionally searchable — FSM-44.)
    excludePageHandles: ['privacy-policy', 'refund-policy', 'terms-conditions', 'wishlist'],
    groupLabels: { products: 'Products', collections: 'Collections', articles: 'Blogs', pages: 'Pages' },
    groupOrder: ['products', 'collections', 'articles', 'pages']
  };

  function debounce(fn, ms) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, ms);
    };
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function highlight(title, term) {
    var safe = escapeHtml(title);
    if (!term) return safe;
    try {
      var re = new RegExp('(' + term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'ig');
      return safe.replace(re, '<mark class="us-hl">$1</mark>');
    } catch (e) { return safe; }
  }

  function suggestUrl(term, limit) {
    return '/search/suggest.json'
      + '?q=' + encodeURIComponent(term)
      + '&resources[type]=' + CONFIG.resources
      + '&resources[limit]=' + limit
      + '&resources[options][unavailable_products]=last'
      + '&resources[options][fields]=' + CONFIG.fields;
  }

  function fetchSuggest(term, limit) {
    return fetch(suggestUrl(term, limit), { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) { return d && d.resources ? d.resources.results : null; })
      .catch(function () { return null; });
  }

  /* ------------------------------------------------------------------ *
   * FSM-44 DETERMINISTIC PRODUCT SEARCH
   * Load the full product index once, then match locally with strict AND.
   * ------------------------------------------------------------------ */
  var INDEX = null;          // array of {h,u,t,img,p,a,new,s}
  var INDEX_PROMISE = null;
  var INDEX_CACHE_KEY = 'us_product_index_v5'; // v5: chunked loading, shopify-taxonomy namespace dropped (perf)

  function parseIndex(text) {
    var s = String(text || '').trim();
    var start = s.indexOf('['), end = s.lastIndexOf(']');
    if (start !== -1 && end !== -1) s = s.slice(start, end + 1);
    try { return JSON.parse(s); } catch (e) { return []; }
  }

  function loadIndex() {
    if (INDEX) return Promise.resolve(INDEX);
    if (INDEX_PROMISE) return INDEX_PROMISE;
    // Session cache: fast repeat searches; refreshes on a new browsing session.
    try {
      var cached = sessionStorage.getItem(INDEX_CACHE_KEY);
      if (cached) { INDEX = JSON.parse(cached); return Promise.resolve(INDEX); }
    } catch (e) { /* ignore */ }
    // FSM-75 perf: the index section paginates by 50 — rendering the whole catalog in
    // one request intermittently exceeded Shopify's render budget (HTTP 500). Fetch the
    // pages in parallel and concatenate; extend if a 4th page comes back full.
    function fetchPage(page) {
      return fetch(CONFIG.indexUrl + '&page=' + page, { headers: { 'Accept': 'text/html' } })
        .then(function (r) { return r.ok ? r.text() : '[]'; })
        .then(parseIndex)
        .catch(function () { return []; });
    }
    INDEX_PROMISE = Promise.all([fetchPage(1), fetchPage(2), fetchPage(3), fetchPage(4)])
      .then(function (pages) {
        var all = [].concat.apply([], pages);
        if (pages[3].length === 50) {                    // catalog grew past 200 — keep going
          return Promise.all([fetchPage(5), fetchPage(6)]).then(function (more) {
            return all.concat(more[0], more[1]);
          });
        }
        return all;
      })
      .then(function (all) {
        INDEX = all;
        // Only cache a non-empty index, so a transient/broken fetch never sticks.
        if (INDEX.length) {
          try { sessionStorage.setItem(INDEX_CACHE_KEY, JSON.stringify(INDEX)); } catch (e) { /* quota */ }
        }
        return INDEX;
      });
    return INDEX_PROMISE;
  }

  // A word matches if it's a substring of the blob, OR (for a plural query word)
  // its singular stem is — so "fans" matches a product that only says "fan".
  // FSM-75 (100-case audit): short (≤2 char) and pure-numeric words match on TOKEN
  // BOUNDARIES instead of substring — "AC" must not match "acrylic"/"compact", and
  // "3 blades" must not match every "38''"/"36W". d.tok is a lazily-built
  // punctuation-stripped token stream (" ac motor 3 blades ").
  function tokStream(d) {
    if (!d.tok) d.tok = ' ' + d.s.replace(/[^a-z0-9"]+/g, ' ') + ' ';
    return d.tok;
  }
  function wordMatches(d, w) {
    var blob = d.s;
    if (w.length <= 2 || /^[0-9]+$/.test(w)) {
      return tokStream(d).indexOf(' ' + w + ' ') !== -1;
    }
    if (blob.indexOf(w) !== -1) return true;
    if (w.length > 3 && w.charAt(w.length - 1) === 's' && blob.indexOf(w.slice(0, -1)) !== -1) return true;
    return false;
  }

  // Strict AND: a product is returned only if EVERY word matches. Deterministic.
  // Ranking: products with all words in the title first, then in-stock.
  function searchIndex(index, term, limit) {
    var words = String(term || '').toLowerCase().trim()
      .replace(/''/g, '"')                       // FSM-75: 56'' -> 56" (match index normalization)
      .split(/\s+/)
      .filter(function (w) { return /[a-z0-9]/.test(w); });  // drop punctuation-only tokens ("+", "-")
    if (!words.length || !index) return [];
    var hits = [];
    for (var i = 0; i < index.length; i++) {
      var d = index[i], ok = true;
      for (var j = 0; j < words.length; j++) {
        if (!wordMatches(d, words[j])) { ok = false; break; }
      }
      if (ok) hits.push(d);
    }
    function titleAll(d) {
      var t = (d.t || '').toLowerCase();
      for (var k = 0; k < words.length; k++) { if (t.indexOf(words[k]) === -1) return false; }
      return true;
    }
    hits.sort(function (a, b) {
      var at = titleAll(a) ? 1 : 0, bt = titleAll(b) ? 1 : 0;
      if (at !== bt) return bt - at;
      return (b.a || 0) - (a.a || 0);
    });
    var mapped = hits.map(function (d) {
      return { url: d.u, title: d.t, image: d.img, price: d.p, available: d.a, isNew: d.new, handle: d.h };
    });
    return limit ? mapped.slice(0, limit) : mapped;
  }

  /* ------------------------------------------------------------------ *
   * FSM-75 (final): /search results grid, rendered client-side from the
   * same index — one matcher for dropdown AND page. (The server-side
   * Liquid matcher intermittently exceeded Shopify's render budget.)
   * ------------------------------------------------------------------ */
  function gridImg(u) {
    return u ? String(u).replace(/width=\d+/, 'width=800') : '';
  }
  function renderResultCard(d) {
    var img = escapeHtml(gridImg(d.image));
    var imgs = img
      ? '<img src="' + img + '" class="lifestyleImg" alt="' + escapeHtml(d.title) + '" loading="lazy">'
        + '<div class="magicImg"><img src="' + img + '" class="defaultImg" alt=""><img src="' + img + '" class="colorImg" alt=""></div>'
      : '';
    var badge = d.available
      ? (d.isNew ? '<div class="tagNew">New</div>' : '')
      : '<div class="outOfStockTag">Out of stock</div>';
    return '<div class="listing_card' + (d.available ? '' : ' outOfStock') + '" data-product-handle="' + escapeHtml(d.handle) + '">'
      + '<a href="' + escapeHtml(d.url) + '" class="mainImg">' + imgs + '</a>'
      + '<div class="floatingItem_flex">' + badge + '</div>'
      + '<a href="' + escapeHtml(d.url) + '" class="product_details"><div class="defaultDetails">'
      + '<h4>' + escapeHtml(d.title) + '</h4>'
      + (d.price ? '<h3><span>&#8377;</span><span class="priceValue">' + escapeHtml(String(d.price)) + '</span> Incl. Tax</h3>' : '')
      + '</div></a></div>';
  }

  function itemThumb(obj) {
    var img = (obj.featured_image && obj.featured_image.url) || obj.image ||
      (obj.featured_media && obj.featured_media.preview_image && obj.featured_media.preview_image.url);
    if (img) return '<span class="us-thumb"><img src="' + escapeHtml(img) + '" alt="" loading="lazy"></span>';
    return '<span class="us-thumb us-thumb--icon"></span>';
  }

  function renderItem(obj, term) {
    return '<a class="us-item" href="' + escapeHtml(obj.url) + '">'
      + itemThumb(obj)
      + '<span class="us-item__title">' + highlight(obj.title, term) + '</span>'
      + '</a>';
  }

  function filterPages(pages) {
    return (pages || []).filter(function (p) {
      var h = p.handle || (p.url || '').split('/').pop();
      return CONFIG.excludePageHandles.indexOf(h) === -1;
    });
  }

  // Build grouped markup. `which` limits which groups to include.
  function buildGroups(results, term, which) {
    if (!results) return '';
    var map = {
      products: results.products || [],
      collections: results.collections || [],
      articles: results.articles || [],
      pages: filterPages(results.pages)
    };
    var html = '';
    CONFIG.groupOrder.forEach(function (key) {
      if (which.indexOf(key) === -1) return;
      var items = map[key];
      if (!items || !items.length) return;
      html += '<div class="us-group" data-group="' + key + '">';
      html += '<div class="us-group__title">' + escapeHtml(CONFIG.groupLabels[key]) + '</div>';
      items.forEach(function (obj) { html += renderItem(obj, term); });
      html += '</div>';
    });
    return html;
  }

  // FSM-44: render a single group from a plain array of item objects.
  function renderGroup(key, items, term, limit) {
    if (!items || !items.length) return '';
    var list = items.slice(0, limit);
    var html = '<div class="us-group" data-group="' + key + '">';
    html += '<div class="us-group__title">' + escapeHtml(CONFIG.groupLabels[key]) + '</div>';
    list.forEach(function (obj) { html += renderItem(obj, term); });
    return html + '</div>';
  }

  /* ---- Header predictive dropdown (all pages) ---- */
  function initDropdown() {
    var input = document.getElementById('headerSearch');
    var panel = document.querySelector('.universal-search__panel');
    if (!input || !panel) return;

    function viewAll(term) {
      return '<a class="us-viewall" href="/search?q=' + encodeURIComponent(term) +
        '">View all results for “' + escapeHtml(term) + '”</a>';
    }
    function open() { panel.hidden = false; input.setAttribute('aria-expanded', 'true'); }
    function close() { panel.hidden = true; input.setAttribute('aria-expanded', 'false'); }

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');

    // FSM-73: prevent empty / whitespace-only searches from submitting to /search.
    var form = input.form || input.closest('form');
    if (form) {
      form.addEventListener('submit', function (e) {
        if (!input.value.trim()) {
          e.preventDefault();
          close();
          input.focus();
        }
      });
    }

    var run = debounce(function () {
      var term = input.value.trim();
      if (term.length < CONFIG.minChars) { close(); return; }
      // FSM-44: products from the deterministic local index (strict AND across all
      // fields incl. metafields); other groups from Predictive.
      Promise.all([
        loadIndex(),
        fetchSuggest(term, CONFIG.dropdownLimit)
      ]).then(function (res) {
        var index = res[0] || [];
        var sugg = res[1] || {};
        var lim = CONFIG.dropdownLimit;
        var products = searchIndex(index, term, lim);
        var groups = renderGroup('products', products, term, lim)
          + renderGroup('collections', sugg.collections, term, lim)
          + renderGroup('articles', sugg.articles, term, lim)
          + renderGroup('pages', filterPages(sugg.pages), term, lim);
        panel.innerHTML = (groups || '<div class="us-empty">No results for “' + escapeHtml(term) + '”</div>') + viewAll(term);
        open();
      });
    }, CONFIG.debounceMs);

    input.addEventListener('input', run);
    // Warm the index as soon as the user engages the box, so the first result is instant.
    input.addEventListener('focus', function () {
      loadIndex();
      if (panel.innerHTML.trim() && input.value.trim().length >= CONFIG.minChars) open();
    });
    document.addEventListener('click', function (e) {
      if (!panel.contains(e.target) && e.target !== input) close();
    });
    input.addEventListener('keydown', function (e) {
      var items = Array.prototype.slice.call(panel.querySelectorAll('.us-item, .us-viewall'));
      if (!items.length) return;
      var idx = items.indexOf(document.activeElement);
      if (e.key === 'ArrowDown') { e.preventDefault(); (items[idx + 1] || items[0]).focus(); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); (items[idx - 1] || items[items.length - 1]).focus(); }
      else if (e.key === 'Escape') { close(); input.blur(); }
    });
  }

  /* ---- /search page: product grid (from the index) + secondary groups ---- */
  function initResultsPage() {
    var grid = document.getElementById('us-results-grid');
    var container = document.getElementById('universal-search-groups');
    if (!grid && !container) return;
    var term = (new URLSearchParams(window.location.search)).get('q') || '';
    if (!term.trim()) return;

    if (grid) {
      loadIndex().then(function (index) {
        var results = searchIndex(index, term, 0);   // 0 = no limit, all matches
        var sort = (new URLSearchParams(window.location.search)).get('sort_by');
        if (sort === 'price-ascending' || sort === 'price-descending') {
          results.sort(function (a, b) {
            var pa = parseInt(String(a.price || '0').replace(/[^0-9]/g, ''), 10) || 0;
            var pb = parseInt(String(b.price || '0').replace(/[^0-9]/g, ''), 10) || 0;
            return sort === 'price-descending' ? pb - pa : pa - pb;
          });
        }
        grid.innerHTML = results.map(renderResultCard).join('');
        var cnt = document.getElementById('usResultsCount');
        if (cnt) cnt.textContent = results.length + (results.length === 1 ? ' Result' : ' Results');
        if (!results.length) {
          var oops = document.getElementById('usNoResults');
          var shell = document.getElementById('usResultsShell');
          if (oops) oops.hidden = false;
          if (shell) shell.hidden = true;
        }
      });
    }

    if (container) {
      fetchSuggest(term, CONFIG.pageLimit).then(function (results) {
        var groups = buildGroups(results, term, ['collections', 'articles', 'pages']);
        if (groups) {
          container.innerHTML = '<div class="us-page-groups__inner container">' + groups + '</div>';
          container.hidden = false;
        }
      });
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    initDropdown();
    initResultsPage();
  });
})();
